export const MATERIALS = [
    'PLASTIC',
    'ALUMINIUM',
    'FERROUS_METALS',
    'PAPER',
    'GLASS',
    'WOOD',
    'MULTIMATERIAL',
    'OTHER',
] as const;

export type MaterialKey = (typeof MATERIALS)[number];

export const MATERIAL_LABELS: Record<MaterialKey, string> = {
    PLASTIC: 'Tworzywa sztuczne',
    ALUMINIUM: 'Aluminium',
    FERROUS_METALS: 'Metale żelazne',
    PAPER: 'Papier i tektura',
    GLASS: 'Szkło',
    WOOD: 'Drewno',
    MULTIMATERIAL: 'Wielomateriałowe',
    OTHER: 'Pozostałe',
};

export const MATERIAL_OPTIONS = MATERIALS.map((value) => ({
    value,
    label: MATERIAL_LABELS[value],
}));

/** Materiały w wierszach 1B tabeli 5.1 (bez wielomateriałowych i „Pozostałe”). */
export const TABLE_51_MATERIALS: MaterialKey[] = [
    'PLASTIC',
    'ALUMINIUM',
    'FERROUS_METALS',
    'PAPER',
    'GLASS',
    'WOOD',
];

export interface PackagingElement {
    id: string;
    name: string;
    isActive: boolean;
}

export interface PackagingItem {
    id?: string;
    elementId: string;
    material: MaterialKey;
    massGrams: number;
    sortOrder?: number;
    sharePercent?: number;
    element?: PackagingElement;
}

export interface PackagingTotals {
    totalGrams: number;
    byMaterial: Partial<Record<MaterialKey, number>>;
    mass51Grams: number;
    mass52Grams: number;
    massOtherGrams: number;
}

export interface ProductPackagingSettings {
    id?: string;
    productId: string;
    includeInBdo: boolean;
    note: string | null;
}

export interface PackagingComment {
    id: string;
    content: string;
    createdAt: string;
    author: { id: string; firstName: string; lastName: string };
    mentionedUserIds?: string[];
}

export interface PackagingFile {
    id: string;
    originalName: string;
    mimeType: string;
    size: number | string;
    createdAt: string;
    uploadedBy?: { firstName: string; lastName: string };
}

export interface ProductPackagingResponse {
    settings: ProductPackagingSettings;
    items: PackagingItem[];
    totals: PackagingTotals;
    comments: PackagingComment[];
    files: PackagingFile[];
}

export interface BatchPackagingResponse {
    batch: {
        id: string;
        lotNumber: string;
        productId: string;
        productName?: string;
    };
    mode: 'PRODUCT_DEFAULT' | 'CUSTOM';
    usesProductDefault: boolean;
    customItems: PackagingItem[];
    productDefaultItems: PackagingItem[];
    items: PackagingItem[];
    totals: PackagingTotals;
}

export interface BdoRange {
    year: number;
    from: string;
    to: string;
}

export interface BdoMaterialRow {
    material: MaterialKey;
    label: string;
    productMg: number;
    collectiveMg: number;
    totalMg: number;
    sharePercent: number;
    bdoRow: string;
}

export interface BdoWarning {
    code: string;
    severity: 'warning';
    message: string;
    linkTab?: 'summary' | 'sales' | 'collective' | 'issues';
    linkFilters?: Record<string, string | number | boolean>;
    count?: number;
}

export interface BdoSummaryResponse {
    range: BdoRange;
    cards: {
        productsMg: number;
        collectiveMg: number;
        total1AMg: number;
        multimaterialMg: number;
    };
    materials: BdoMaterialRow[];
    multimaterial: {
        productMg: number;
        collectiveMg: number;
        totalMg: number;
    };
    totals: {
        productsMg: number;
        collectiveMg: number;
        total1AMg: number;
        total51Mg: number;
        multimaterialMg: number;
    };
    warnings: BdoWarning[];
    stats: BdoStats;
    generatedAt: string;
}

export interface BdoStats {
    ordersCount: number;
    linesCount: number;
    productsCount: number;
    linesWithoutProduct: number;
    linesWithoutComposition: number;
    linesWithoutBatch: number;
    productsWithoutComposition: number;
    emptyCustomBatches: number;
    suspiciousShipmentOrders: number;
    ordersWithoutCountry: number;
    excludedProducts: number;
    skippedExcludedLines: number;
    firstSaleDate: string | null;
    lastSaleDate: string | null;
}

export interface BdoSalesRow {
    orderId: string;
    orderNumber: string;
    date: string;
    productId: string | null;
    productName: string;
    sku: string;
    quantity: number;
    batchId: string | null;
    batchLotNumber: string | null;
    compositionSource: string;
    totalGrams: number;
    totalMg: number;
    materials: Record<string, number>;
}

export interface BdoSalesResponse {
    range: BdoRange;
    rows: BdoSalesRow[];
    total: number;
    page: number;
    limit: number;
}

export interface BdoCollectiveEntry {
    id: string;
    date: string;
    material: MaterialKey;
    massGrams: number | string;
    description?: string;
    createdBy?: { firstName: string; lastName: string };
    createdAt: string;
}

export interface BdoIssuesResponse {
    range: BdoRange;
    warnings: BdoWarning[];
    stats: BdoStats;
}

export function formatMg(value: number | string | null | undefined): string {
    return Number(value || 0).toFixed(4);
}

export function formatGrams(value: number | string | null | undefined): string {
    return Number(value || 0).toFixed(2);
}
